<template>
	<div class="ellipsis-wrap">
		<slot name="ellipsis-label"/>
		<span
			:class="[
				'ellipsis-wrap__text',
				!maxLengthDisplayedCharacters && 'has-css-ellipsis'
			]"
			ref="ellipsisTextRef"
			@focus="displayTooltip"
			@mouseover="displayTooltip"
			@mouseleave="hideTooltip"
			v-html="truncate"
		/>
		<Tooltip
			:text="data"
			:display="showTooltip && (trucanted || hasTruncatedCharacters)"
			:trackedRefName="'ellipsisTextRef'"
			:position="position !== 'top-start' ? position : 'top-start'"
			:offset="!offset.length ? [0, 4] : offset"
		/>
	</div>
</template>

<script>
import Tooltip from '@/components/shells/tooltip/tooltip';

// mixins
import DocumentClick from '@/mixins/document-click';

// utils
import { createTextValueObj } from '@/utils/object';

export default {
	name: 'Ellipsis',
	components: {
		Tooltip
	},
	mixins: [DocumentClick('forceHideTooltip')],
	model: {
		prop: 'data'
	},
	props: {
		data: {
			type: String,
			default: ''
		},
		delayTime: {
			type: Number,
			default: 20
		},
		position: {
			type: String,
			default: 'top-start',
			validator: function validator(value) {
				return (
					[
						'auto',
						'auto-start',
						'auto-end',
						'top',
						'top-start',
						'top-end',
						'bottom',
						'bottom-start',
						'bottom-end',
						'right',
						'right-start',
						'right-end',
						'left',
						'left-start',
						'left-end'
					].indexOf(value) > -1
				);
			}
		},
		offset: {
			type: Array,
			default: () => []
		},
		/* maxLengthDisplayedCharacters: forcing truncate modifing the text */
		maxLengthDisplayedCharacters: {
			type: Number,
			default: null
		},
		textToHighlight: {
			type: String,
			default: ''
		},
		timeout: {
			type: Number,
			default: -1
		}
	},
	data() {
		return {
			showTooltip: false,
			trucanted: false,
			resizeEventListener: null,
			timer: null
		};
	},
	computed: {
		hasTruncatedCharacters() {
			if (!this.maxLengthDisplayedCharacters || !this.data) {
				return false;
			}

			return (
				(this.data.trim() || '').length >
				this.maxLengthDisplayedCharacters
			);
		},
		truncate() {
			let words = (this.data || '').trim();
			if (
				this.maxLengthDisplayedCharacters &&
				this.hasTruncatedCharacters
			) {
				const truncateText =
					words.substring(0, this.maxLengthDisplayedCharacters) +
					'...';

				return createTextValueObj(truncateText, this.textToHighlight);
			}
			return createTextValueObj(words, this.textToHighlight);
		}
	},
	methods: {
		hasTruncate() {
			if (this.$refs && this.$refs.ellipsisTextRef) {
				this.trucanted =
					this.$refs.ellipsisTextRef.clientWidth <
					this.$refs.ellipsisTextRef.scrollWidth;
			}
		},
		displayTooltip() {
			if (this.timeout === -1) {
				this.showTooltip = true;
				return;
			}

			this.showTooltip = true;
			this.setTimeout();
		},
		hideTooltip() {
			if (this.timeout === -1) {
				this.showTooltip = false;
			}
		},
		forceHideTooltip() {
			if (this.timeout > 0) {
				this.freeTimeout();
				this.showTooltip = false;
			}
		},
		setTimeout() {
			this.freeTimeout();
			this.timer = setTimeout(() => {
				this.showTooltip = false;
			}, this.timeout);
		},
		freeTimeout() {
			if (this.timer) {
				clearTimeout(this.timer);
				this.timer = null;
			}
		}
	},
	mounted() {
		this.resizeEventListener = window.addEventListener('resize', () => {
			setTimeout(() => {
				this.hasTruncate();
			}, this.delayTime || 0);
		});
	},
	updated() {
		this.hasTruncate();
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.resizeEventListener);
	},
	watch: {
		data: {
			immediate: true,
			handler() {
				this.$nextTick(() => {
					this.hasTruncate();
				});
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.ellipsis-wrap {
	display: flex;
	overflow: hidden;

	&__text.has-css-ellipsis {
		@include ellipsis;
	}

	.tooltip {
		z-index: 30;
	}
}
</style>
